/**
 * The wire shapes both case surfaces read, transcribed from `backend-support`'s
 * own PRD-TDD. That service names its body fields `camelCase`, and the console
 * follows the side that serves them.
 *
 * Local declarations only because `src/shared/api/generated/` is still empty.
 */

export type CaseId = string;

/**
 * One row of the queue — `GET /admin/cases`, ordered by last activity.
 *
 * **A case has no title.** What names it is its `reason`, drawn from the
 * configured list for its own type — and the defect type configures no list at
 * all («free text; no fixed reason list»), so its rows carry no reason and the
 * surface names them by the first line of what was written instead.
 *
 * `subjectLabel` and `subjectOwnerUserId` are **snapshots taken at raise time**
 * on the two types that have a subject. On a platform where the catalogue delete
 * is hard, the snapshot is the only surviving human name of a title that was
 * removed — so the surface renders the snapshot and the link resolves live.
 */
export interface CaseRow {
  id: CaseId;
  caseReference: string;
  /** One of the five code-owned types; a sixth would render as its own token. */
  caseType: string;
  /** The configured `CASE_STATES` member, stored verbatim. */
  caseState: string;
  reason?: string | null;
  /** Who raised it. The service resolves the identity; the row stores an id. */
  raiser?: { id: string; displayName?: string | null; handle?: string | null } | null;
  subjectType?: string | null;
  subjectId?: string | null;
  subjectLabel?: string | null;
  /**
   * The account that owns the reported subject — a snapshot ULID, and a name
   * only where the service resolves one.
   *
   * It is read on the two report types and nowhere else. The decision's block
   * rider aims at this account, so the surface that offers the rider has to be
   * able to name who it would block: a checkbox that blocks an unnamed party is
   * not a decision an operator can take.
   */
  subjectOwnerUserId?: string | null;
  subjectOwnerDisplayName?: string | null;
  lastActivityAt: string;
}

/**
 * Keyset paging over `(last_activity_at, id)` with an opaque cursor. The queue
 * draws no traversal control, so the console holds no cursor and composes none.
 */
export interface CasePage {
  items: CaseRow[];
  page_size?: number;
  total?: number;
  has_next?: boolean;
  next_cursor?: string | null;
}

/**
 * A page of cases **exactly as `backend-support` states it** — `pageSize`,
 * `hasNext`, `nextCursor`, and a `total` this console was not reading either.
 *
 * `backend-support` names its body fields `camelCase`; the row shapes above
 * already follow it. The page envelope did not, so «التالي» never enabled and
 * the cursor was never built — every case past the first page was unreachable
 * on a surface whose whole purpose is working a queue to the bottom.
 */
export interface WireCasePage {
  items: CaseRow[];
  pageSize?: number;
  total?: number;
  hasNext?: boolean;
  nextCursor?: string | null;
}

/** Translated once, here, rather than at the surface that reads it. */
export function toCasePage(wire: WireCasePage): CasePage {
  return {
    items: wire.items ?? [],
    ...(wire.pageSize === undefined ? {} : { page_size: wire.pageSize }),
    ...(wire.total === undefined ? {} : { total: wire.total }),
    has_next: Boolean(wire.hasNext),
    next_cursor: wire.nextCursor ?? null,
  };
}

/**
 * One message in the thread.
 *
 * `authorSide` is **data, not a guess**: the owning service stores `RAISER` or
 * `OPERATOR` on the row precisely so a surface never derives the party from a
 * role lookup that may have changed since the message was written.
 *
 * `authorDisplayName` is optional because the message table stores an id and no
 * name. Where the service resolves none, the surface names the **side** rather
 * than inventing a person.
 */
export interface CaseMessage {
  id: string;
  authorSide: 'RAISER' | 'OPERATOR' | string;
  authorUserId?: string | null;
  authorDisplayName?: string | null;
  body: string;
  createdAt: string;
}

/** One case, its facts, its thread, and — on a content report — its resolution. */
export interface CaseDetail extends CaseRow {
  createdAt: string;
  messages: CaseMessage[];
  resolvedAt?: string | null;
  closedAt?: string | null;
  /**
   * Present once a content report has been decided; the three buttons disable
   * behind it. Absent on every other type, which has no decision to take.
   */
  resolution?: {
    resolutionAction: string;
    blockedOwner?: boolean;
    resolvedAt: string;
  } | null;
  /** The reported title's publish state, where the service resolves one. */
  subjectPublishState?: string | null;
}

/**
 * The three resolutions catalog §20.8 names, and no fourth.
 *
 * They apply to a **content report only** — a user report is worked through the
 * reply and the state control, and the block that answers it is taken from that
 * account's own record on the user surface. The owning service enforces the same
 * narrowing with `422 SUPPORT_RESOLUTION_NOT_APPLICABLE`, so the surface and the
 * wire agree rather than one of them guarding alone.
 */
export type ResolutionAction = 'DISMISS' | 'UNPUBLISH_SUBJECT' | 'DELETE_SUBJECT';

/** The one case type the decision applies to. */
export const RESOLVABLE_CASE_TYPE = 'CONTENT_REPORT';

/**
 * `GET /api/v1/admin/cases/{caseId}` **exactly as `backend-support` states it**.
 *
 * The service answers `ModelGetCaseOutputForAdmin` — `{ case, messages }`, the
 * row nested under its own key beside the thread — and this console read it as
 * though the facts were flat beside `messages`. Every field of the case was
 * therefore `undefined`, and the first one the page touched was `createdAt`:
 * `toMachineDate(undefined)` is `new Date(undefined).toISOString()`, which
 * throws `RangeError: Invalid time value` **during render**. The router's error
 * element caught it, and because a thrown `RangeError` is not an `ApiError` the
 * fallback defaulted to its `500` copy — so a page whose every request returned
 * `200` reported a service failure, and the reference it offered was the id of
 * an unrelated call. The queue beside it was unaffected: the list already
 * translated its envelope through `toCasePage`, and only the detail did not.
 *
 * Translated once, here, for the same reason the page envelope is: a surface
 * that reshapes a payload inline is a surface where the next drift is invisible.
 */
export interface WireCaseDetail {
  case: Omit<CaseDetail, 'messages'>;
  messages: CaseMessage[];
}

/** The wire's two halves, flattened into the shape both case surfaces read. */
export function toCaseDetail(wire: WireCaseDetail): CaseDetail {
  return { ...wire.case, messages: wire.messages ?? [] };
}

/**
 * `GET /api/v1/caseStates` — `{value, ordinal}` and nothing else.
 *
 * **No colour, no badge kind, no label.** The design system owns the badge
 * mapping out of a closed taxonomy, and each surface owns its own wording:
 * «بانتظار المستخدم» in this queue and «بانتظار المُبلِّغ» on the report
 * surfaces are one value read twice. A backend that shipped the label would
 * make that impossible.
 */
export interface CaseStateRow {
  value: string;
  ordinal: number;
}

/** `PATCH /admin/cases/{id}` — the state control's response. */
export interface CaseTransitionResult {
  caseState: string;
  resolvedAt?: string | null;
  closedAt?: string | null;
  lastActivityAt: string;
}
